import React, { useState, useMemo, useEffect, useRef } from "react";
import { getOrders } from "../../data/orders";
import { getCatalog } from "../../data/catalog";

const formatPrice = (v) => `${Number(v || 0).toFixed(2)} €`;

const RETURN_REASONS = [
  { value: "defectueux", label: "Défectueux" },
  { value: "errone", label: "Erroné" },
  { value: "annulation", label: "Annulation client" },
  { value: "autre", label: "Autre" },
];

const ReturnModal = ({ show, onClose, onAddReturnItem }) => {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("TICKET"); // TICKET or EXPRESS
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  
  // For Express Return
  const [expressBarcode, setExpressBarcode] = useState("");
  const barcodeRef = useRef(null);

  useEffect(() => { 
    if (show) {
      setCatalog(getCatalog()); 
      setStep(1);
      setSearchQuery("");
      setSelectedOrder(null);
      setReturnItems([]);
      setExpressBarcode("");
    }
  }, [show]);

  useEffect(() => {
    if (mode === "EXPRESS" && barcodeRef.current && show) {
      setTimeout(() => barcodeRef.current.focus(), 100);
    }
  }, [mode, show]);

  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return getOrders().filter(
      (o) =>
        o.orderNumber?.toLowerCase().includes(q) ||
        o.client?.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const eligibleItems = useMemo(() => {
    if (!selectedOrder) return [];
    return (selectedOrder.items || []).filter((item) => !item.isReturn);
  }, [selectedOrder]);

  const hasSelectableItems = eligibleItems.length > 0;

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setReturnItems(
      (order.items || []).filter((a) => !a.isReturn).map((item) => {
        const product = catalog.find((p) => p.id === item.productId) || { id: item.productId, name: item.name, price: item.unitPrice, barcode: "" };
        return {
          product,
          originalItem: item,
          quantity: 0,
          restock: true,
          reason: "defectueux",
        };
      })
    );
    setStep(2);
  };

  const handleItemQtyChange = (index, val) => {
    const newItems = [...returnItems];
    const r = newItems[index];
    if (val < 0) val = 0;
    if (val > r.originalItem.quantity) val = r.originalItem.quantity;
    r.quantity = val;
    setReturnItems(newItems);
  };

  const handleItemRestockToggle = (index) => {
    const newItems = [...returnItems];
    newItems[index].restock = !newItems[index].restock;
    setReturnItems(newItems);
  };

  const handleItemReasonChange = (index, reason) => {
    const newItems = [...returnItems];
    newItems[index].reason = reason;
    setReturnItems(newItems);
  };

  const handleConfirmReturn = () => {
    const toReturn = returnItems.filter((r) => r.quantity > 0);
    toReturn.forEach((r) => {
      onAddReturnItem(
        r.product,
        r.quantity,
        r.restock,
        r.reason,
        selectedOrder.orderNumber,
        r.originalItem.unitPrice
      );
    });
    onClose();
  };
  
  const handleExpressReturn = (e) => {
    e.preventDefault();
    if (!expressBarcode.trim()) return;
    
    // Search in catalog by barcode or name
    const q = expressBarcode.toLowerCase();
    const product = catalog.find(p => p.barcode === q || p.name.toLowerCase() === q);
    
    if (product) {
      onAddReturnItem(
        product,
        1, // Default quantity
        true, // Restock by default
        "autre",
        "RETOUR_EXPRESS",
        product.price
      );
      onClose();
    } else {
      alert("Produit introuvable dans le catalogue. Veuillez créer le produit d d'abord.");
    }
  };

  if (!show) return null;

  const anyItemSelected = returnItems.some((r) => r.quantity > 0);

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg border-0" style={{ backgroundColor: "#1f2937", color: "white", borderRadius: "12px" }}>
          <div className="modal-header border-bottom-0 pt-4 pb-3">
            <h4 className="modal-title fw-bold text-danger">
              <i className="bi bi-arrow-return-left me-2"></i> RETOUR PRODUIT
            </h4>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          
          <div className="modal-body p-4">
            
            {step === 1 && (
              <>
                <div className="d-flex justify-content-center gap-3 mb-4">
                  <button 
                    className={`btn fw-bold px-4 ${mode === "TICKET" ? "btn-danger" : "btn-outline-light"}`}
                    onClick={() => setMode("TICKET")}
                  >
                    VIA TICKET DE CAISSE
                  </button>
                  <button 
                    className={`btn fw-bold px-4 ${mode === "EXPRESS" ? "btn-danger" : "btn-outline-light"}`}
                    onClick={() => setMode("EXPRESS")}
                  >
                    RETOUR EXPRESS (SANS TICKET)
                  </button>
                </div>
                
                {mode === "TICKET" ? (
                  <div className="bg-dark p-4 rounded border border-secondary">
                    <div className="mb-4">
                      <label className="form-label small fw-bold text-light opacity-75">Rechercher une commande</label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-secondary border-secondary text-white">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control bg-dark text-white border-secondary"
                          placeholder="N° Commande, Nom client..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="list-group rounded" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {searchQuery && filteredOrders.length === 0 ? (
                        <div className="text-center text-muted py-4 border border-secondary rounded">
                          <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50"></i>
                          Aucune commande trouvée
                        </div>
                      ) : (
                        filteredOrders.map((order) => {
                          const itemCount = (order.items || []).filter((i) => !i.isReturn).length;
                          return (
                            <button
                              key={order.id}
                              className="list-group-item list-group-item-action bg-dark text-white border-secondary d-flex justify-content-between align-items-center"
                              onClick={() => handleSelectOrder(order)}
                            >
                              <div>
                                <div className="fw-bold text-info">
                                  {order.orderNumber || order.id}
                                </div>
                                <div className="text-muted small">
                                  {order.client || "Client comptoir"}
                                  {order.date ? ` · ${order.date}` : ""}
                                </div>
                              </div>
                              <div className="text-end">
                                <div className="fw-bold text-white">
                                  {formatPrice(order.total)}
                                </div>
                                <div className="text-muted small">
                                  {itemCount} article{itemCount !== 1 ? "s" : ""}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-dark p-5 rounded border border-secondary text-center">
                    <i className="bi bi-upc-scan fs-1 text-muted mb-3 d-block"></i>
                    <h5 className="mb-4">Scannez l'article à retourner</h5>
                    <form onSubmit={handleExpressReturn} className="mx-auto" style={{ maxWidth: "400px" }}>
                      <div className="input-group input-group-lg mb-3">
                        <input 
                          ref={barcodeRef}
                          type="text" 
                          className="form-control bg-dark text-white border-secondary text-center fw-bold" 
                          placeholder="Code-barres ou nom..."
                          value={expressBarcode}
                          onChange={(e) => setExpressBarcode(e.target.value)}
                        />
                      </div>
                      <button type="submit" className="btn btn-danger btn-lg w-100 fw-bold" disabled={!expressBarcode.trim()}>
                        AJOUTER AU TICKET DE RETOUR
                      </button>
                    </form>
                    <p className="small text-muted mt-4">
                      Le produit sera ajouté au ticket courant avec une quantité négative.<br/>
                      Vous pourrez modifier la quantité directement sur le ticket.
                    </p>
                  </div>
                )}
              </>
            )}

            {step === 2 && selectedOrder && (
              <div>
                <div className="d-flex align-items-center mb-4 border-bottom border-secondary pb-3">
                  <button className="btn btn-sm btn-outline-light me-3" onClick={() => setStep(1)}>
                    <i className="bi bi-arrow-left"></i>
                  </button>
                  <h5 className="mb-0 fw-bold text-info">
                    Commande {selectedOrder.orderNumber}
                  </h5>
                </div>

                {!hasSelectableItems ? (
                  <div className="text-center text-muted py-5 bg-dark rounded border border-secondary">
                    <i className="bi bi-check-circle fs-1 d-block mb-2 opacity-50"></i>
                    Aucun article éligible au retour.
                  </div>
                ) : (
                  <>
                    <div className="rounded bg-dark border border-secondary mb-4" style={{ maxHeight: "350px", overflowY: "auto" }}>
                      {returnItems.map((r, index) => (
                        <div key={index} className="p-3 border-bottom border-secondary">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="fw-bold">{r.originalItem.name}</div>
                            <div className="text-muted small">
                              {r.originalItem.quantity} × {formatPrice(r.originalItem.unitPrice)}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-3 flex-wrap bg-secondary bg-opacity-25 p-2 rounded">
                            <div className="d-flex align-items-center">
                              <label className="text-light small fw-bold me-2 mb-0">Qté à retourner :</label>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center fw-bold bg-dark text-white border-secondary"
                                style={{ width: "70px" }}
                                min={0}
                                max={r.originalItem.quantity}
                                value={r.quantity}
                                onChange={(e) => handleItemQtyChange(index, parseInt(e.target.value, 10) || 0)}
                              />
                            </div>

                            <div className="form-check form-switch ms-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`restock-${index}`}
                                checked={r.restock}
                                onChange={() => handleItemRestockToggle(index)}
                              />
                              <label className="form-check-label small text-light" htmlFor={`restock-${index}`}>
                                Remettre en stock
                              </label>
                            </div>

                            <select
                              className="form-select form-select-sm bg-dark text-white border-secondary ms-auto"
                              style={{ width: "auto", minWidth: "140px" }}
                              value={r.reason}
                              onChange={(e) => handleItemReasonChange(index, e.target.value)}
                            >
                              {RETURN_REASONS.map((reason) => (
                                <option key={reason.value} value={reason.value}>
                                  {reason.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex justify-content-between">
                      <button className="btn btn-secondary px-4 fw-bold" onClick={() => setStep(1)}>
                        RETOUR
                      </button>
                      <button
                        className={`btn px-5 fw-bold text-white ${anyItemSelected ? "btn-danger" : "btn-secondary"}`}
                        disabled={!anyItemSelected}
                        onClick={handleConfirmReturn}
                      >
                        <i className="bi bi-check2-circle me-2"></i>
                        VALIDER LE RETOUR
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnModal;

